import type { EventPayloads, EventRow, Question } from "./types.ts";

export interface ToolCallView {
  id: string;
  name: string;
  input: Record<string, unknown>;
  at: string;
  result?: { isError: boolean; text: string; at: string };
}

export interface Resolution {
  via: string;
  by: string;
  summary: string;
  at: string;
}

export type TranscriptItem =
  | { kind: "user"; seq: number; at: string; text: string; via: string; author?: string }
  | { kind: "assistant"; seq: number; at: string; text: string }
  | { kind: "tools"; seq: number; at: string; end: string; calls: ToolCallView[] }
  | { kind: "question"; seq: number; at: string; promptId: string; questions: Question[]; resolution?: Resolution }
  | { kind: "permission"; seq: number; at: string; promptId: string; payload: EventPayloads["permission"]; resolution?: Resolution }
  | { kind: "result"; seq: number; at: string; payload: EventPayloads["result"] }
  | { kind: "error"; seq: number; at: string; payload: EventPayloads["error"] }
  | { kind: "system"; seq: number; at: string; text: string; tone: "muted" | "warn" | "bad" | "ok" };

const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

/** Folds the event log into what the transcript shows: messages, tool bursts, prompts with their answers, results. */
export function buildTranscript(events: readonly EventRow[]): { items: TranscriptItem[]; tools: ToolCallView[] } {
  const items: TranscriptItem[] = [];
  const tools: ToolCallView[] = [];
  const toolById = new Map<string, ToolCallView>();
  const prompts = new Map<string, Extract<TranscriptItem, { kind: "question" | "permission" }>>();
  let burst: Extract<TranscriptItem, { kind: "tools" }> | null = null;

  for (const e of events) {
    const at = e.createdAt;
    const p = e.payload as never;
    if (e.type !== "tool_use" && e.type !== "tool_result" && e.type !== "state_change" && e.type !== "api_retry" && e.type !== "rate_limit") burst = null;
    switch (e.type) {
      case "user_prompt": {
        const u = p as EventPayloads["user_prompt"];
        items.push({ kind: "user", seq: e.seq, at, text: u.text, via: u.via, author: u.author });
        break;
      }
      case "assistant_text":
        items.push({ kind: "assistant", seq: e.seq, at, text: (p as EventPayloads["assistant_text"]).text });
        break;
      case "tool_use": {
        const t = p as EventPayloads["tool_use"];
        const call: ToolCallView = { id: t.toolUseId, name: t.name, input: asRecord(t.input), at };
        tools.push(call);
        toolById.set(call.id, call);
        if (!burst) {
          burst = { kind: "tools", seq: e.seq, at, end: at, calls: [] };
          items.push(burst);
        }
        burst.calls.push(call);
        burst.end = at;
        break;
      }
      case "tool_result": {
        const r = p as EventPayloads["tool_result"];
        const call = toolById.get(r.toolUseId);
        if (call) call.result = { isError: r.isError, text: r.text, at };
        if (burst) burst.end = at;
        break;
      }
      case "question": {
        const q = p as EventPayloads["question"];
        const item = { kind: "question" as const, seq: e.seq, at, promptId: q.promptId, questions: q.questions };
        prompts.set(q.promptId, item);
        items.push(item);
        break;
      }
      case "permission": {
        const q = p as EventPayloads["permission"];
        const item = { kind: "permission" as const, seq: e.seq, at, promptId: q.promptId, payload: q };
        prompts.set(q.promptId, item);
        items.push(item);
        break;
      }
      case "prompt_resolved": {
        const r = p as EventPayloads["prompt_resolved"];
        const item = prompts.get(r.promptId);
        if (item) item.resolution = { via: r.via, by: r.by, summary: r.summary, at };
        break;
      }
      case "result":
        items.push({ kind: "result", seq: e.seq, at, payload: p });
        break;
      case "error":
        items.push({ kind: "error", seq: e.seq, at, payload: p });
        break;
      case "state_change": {
        const s = p as EventPayloads["state_change"];
        const line = stateLine(s);
        if (line) items.push({ kind: "system", seq: e.seq, at, ...line });
        break;
      }
      default:
        break;
    }
  }
  return { items, tools };
}

function stateLine(s: EventPayloads["state_change"]): { text: string; tone: "muted" | "warn" | "bad" | "ok" } | null {
  if (s.from === "created" && s.to === "created") return { text: "Worktree ready", tone: "muted" };
  if (s.to === "idle" && s.reason === "started") return { text: "Session started", tone: "ok" };
  if (s.to === "idle" && s.reason === "resumed") return { text: "Session resumed", tone: "ok" };
  if (s.to === "idle" && s.reason === "runner reconnected") return { text: "Runner reconnected", tone: "muted" };
  if (s.to === "stopped") return { text: `Stopped${s.reason ? ` · ${s.reason}` : ""}`, tone: "muted" };
  if (s.to === "errored") return { text: `Errored${s.reason ? ` · ${s.reason}` : ""}`, tone: "bad" };
  if (s.to === "rate_limited") return { text: "Rate limited · the runner keeps retrying", tone: "warn" };
  if (s.to === "queued") return { text: `Queued${s.reason ? ` · ${s.reason}` : ""}`, tone: "muted" };
  return null;
}

/** When the current turn started (last switch into Working from a resting state). */
export function turnStartedAt(events: readonly EventRow[]): string | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    if (e.type !== "state_change") continue;
    const s = e.payload as EventPayloads["state_change"];
    if (s.to === "working" && !["working", "waiting", "rate_limited"].includes(s.from)) return e.createdAt;
  }
  return null;
}

/** "Read auth/login.ts" style label for one tool call. */
export function toolLabel(call: Pick<ToolCallView, "name" | "input">): string {
  const i = call.input;
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const file = str(i.file_path) || str(i.notebook_path) || str(i.path);
  switch (call.name) {
    case "Bash":
      return str(i.command).replace(/\s+/g, " ").slice(0, 120);
    case "Read":
    case "Edit":
    case "MultiEdit":
    case "Write":
    case "NotebookEdit":
      return file;
    case "Grep":
      return `"${str(i.pattern)}"${file ? ` in ${file}` : ""}`;
    case "Glob":
      return str(i.pattern);
    case "WebFetch":
      return str(i.url);
    case "WebSearch":
      return str(i.query);
    case "Task":
    case "Agent":
      return str(i.description) || str(i.prompt).slice(0, 80);
    case "TodoWrite":
      return Array.isArray(i.todos) ? `${i.todos.length} items` : "";
    default:
      return JSON.stringify(i).slice(0, 100);
  }
}
